import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  DonutChart,
  BarChart,
  Sparkline,
  ProgressRing,
  useCountUp,
} from '../../Components/AdminCharts';
import './AdminDashboard.css';
import '../../Components/AdminCharts.css';

const CONTACT_API_URL = 'https://api.avtechbmt.cloud/api/contact';

/* -------------------------------------------------------------------------- */
/* Metric definitions                                                          */
/* -------------------------------------------------------------------------- */
const METRICS = [
  { key: 'users',      label: 'Registered Users',  icon: '👥', tone: 'blue',    tab: 'users',      color: '#2563eb' },
  { key: 'courses',    label: 'Active Courses',    icon: '📚', tone: 'indigo',  tab: 'courses',    color: '#4f46e5' },
  { key: 'exams',      label: 'Active Exams',      icon: '📝', tone: 'violet',  tab: 'exams',      color: '#7c3aed' },
  { key: 'candidates', label: 'Candidates',        icon: '🎓', tone: 'emerald', tab: 'candidates', color: '#059669' },
  { key: 'gallery',    label: 'Gallery Media',     icon: '🖼️', tone: 'amber',   tab: 'gallery',    color: '#d97706' },
  { key: 'messages',   label: 'Contact Inquiries', icon: '📬', tone: 'rose',    tab: 'messages',   color: '#e11d48' },
];

const QUICK_ACTIONS = [
  { key: 'courses',    icon: '📚', title: 'Manage Courses', sub: 'Create, edit or archive' },
  { key: 'exams',      icon: '📝', title: 'Manage Exams',   sub: 'Schedule and review' },
  { key: 'candidates', icon: '🎓', title: 'Candidates',     sub: 'Review applications' },
  { key: 'messages',   icon: '📬', title: 'Inbox',          sub: 'Reply to inquiries' },
];

/* Fake historical sparkline seed for each metric (purely visual) */
const SPARK_SEEDS = {
  users:      [4, 6, 5, 8, 7, 10, 12],
  courses:    [2, 3, 3, 5, 4, 6, 7],
  exams:      [1, 2, 4, 3, 5, 6, 8],
  candidates: [3, 5, 4, 6, 8, 7, 9],
  gallery:    [1, 2, 2, 3, 4, 3, 5],
  messages:   [2, 3, 5, 4, 6, 7, 6],
};

export default function AdminDashboard({
  usersCount = 0,
  galleryCount = 0,
  messagesCount = 0,
  candidatesCount = 0,
  examsCount = 0,
  coursesCount = 0,
  onSelectTab,
}) {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecentMessages = async () => {
    const authToken = token || localStorage.getItem('token');
    try {
      setLoading(true);
      const res = await fetch(`${CONTACT_API_URL}/all`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        const all = data.data || [];
        setMessages(all.slice(0, 8));
      }
    } catch (err) {
      console.error('Error fetching dashboard messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const goTo = (tab) => {
    if (onSelectTab) onSelectTab(tab);
    else navigate(`/admin/${tab}`);
  };

  const metricValues = useMemo(
    () => ({
      users: usersCount,
      courses: coursesCount,
      exams: examsCount,
      candidates: candidatesCount,
      gallery: galleryCount,
      messages: messagesCount,
    }),
    [usersCount, coursesCount, examsCount, candidatesCount, galleryCount, messagesCount]
  );

  const getTrend = (value) =>
    value > 0
      ? { type: 'up', text: '▲ Live' }
      : { type: 'neutral', text: '— Empty' };

  const greeting = getGreeting(user?.name);

  /* ------------------------------------------------------------------
     Chart data derivation
     ------------------------------------------------------------------ */

  /* Area chart — inquiries received per day over the last 7 days.
     Falls back to a friendly demo curve if no messages loaded yet. */
  const areaData = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const buckets = [0, 0, 0, 0, 0, 0, 0];
    const now = new Date();

    messages.forEach((m) => {
      if (!m.createdAt) return;
      const d = new Date(m.createdAt);
      const diffDays = Math.floor((now - d) / 86400000);
      if (diffDays >= 0 && diffDays < 7) {
        const idx = d.getDay();
        buckets[idx] += 1;
      }
    });

    // If no messages yet, show a light demo trend so the chart isn't empty
    const hasAny = buckets.some((b) => b > 0);
    const finalBuckets = hasAny ? buckets : [1, 2, 1, 3, 2, 4, 3];

    return finalBuckets.map((v, i) => ({ label: days[i], value: v }));
  }, [messages]);

  /* Donut chart — content distribution across modules */
  const donutData = useMemo(
    () => [
      { label: 'Users',      value: usersCount || 0 },
      { label: 'Courses',    value: coursesCount || 0 },
      { label: 'Exams',      value: examsCount || 0 },
      { label: 'Candidates', value: candidatesCount || 0 },
      { label: 'Gallery',    value: galleryCount || 0 },
      { label: 'Messages',   value: messagesCount || 0 },
    ],
    [usersCount, coursesCount, examsCount, candidatesCount, galleryCount, messagesCount]
  );

  /* Bar chart — top modules by count */
  const barData = useMemo(
    () => donutData.map((d) => ({ label: d.label, value: d.value })),
    [donutData]
  );

  /* Progress ring — overall content fill % (relative to a soft target) */
  const totalContent = donutData.reduce((s, d) => s + d.value, 0);
  const contentTarget = Math.max(totalContent, 100);

  return (
    <div className="dashboard-container">
      {/* ==================== HERO ==================== */}
      <section className="dashboard-hero">
        <div className="dashboard-hero-text">
          <h2>
            {greeting}, {user?.name?.split(' ')[0] || 'Admin'} 👋
          </h2>
          <p>
            Live overview of platform state, resources, and recent activity.
            Use the sidebar to dive into any module.
          </p>
          <span className="dashboard-hero-meta">
            <span className="status-dot" />
            All systems operational · Updated just now
          </span>
        </div>

        <div className="dashboard-hero-actions">
          <button className="ghost-btn" onClick={fetchRecentMessages}>
            ↻ Refresh Activity
          </button>
        </div>
      </section>

      {/* ==================== METRICS ==================== */}
      <section className="metrics-grid">
        {METRICS.map((m, idx) => {
          const value = metricValues[m.key] ?? 0;
          const trend = getTrend(value);
          return (
            <MetricCard
              key={m.key}
              metric={m}
              value={value}
              trend={trend}
              sparkSeed={SPARK_SEEDS[m.key]}
              delay={idx * 60}
              onClick={() => goTo(m.tab)}
            />
          );
        })}

        {/* System health card */}
        <div className="metric-card health">
          <div className="metric-top">
            <span className="metric-icon tone-emerald">❤️</span>
            <span className="metric-trend up">▲ Stable</span>
          </div>
          <p className="metric-label">System Health</p>
          <p className="metric-value">
            <span className="status-dot" />
            Operational
          </p>
          <div className="metric-foot">
            <span>API · DB · Storage</span>
          </div>
        </div>
      </section>

      {/* ==================== ANALYTICS CHARTS ==================== */}
      <section className="charts-row charts-row-2">
        <AreaChart
          data={areaData}
          title="Contact Inquiries"
          subtitle="Messages received per day"
          color="#4f46e5"
          height={230}
        />

        <DonutChart
          data={donutData}
          title="Content Distribution"
          subtitle="Across all modules"
          size={200}
          thickness={22}
        />
      </section>

      <section className="charts-row charts-row-3">
        <BarChart
          data={barData}
          title="Module Activity"
          subtitle="Records per module"
          height={200}
        />

        <div className="chart-card ring-card">
          <div className="chart-card-head">
            <div>
              <h3 className="chart-card-title">Platform Fill</h3>
              <span className="chart-card-sub">
                Content vs. target volume
              </span>
            </div>
          </div>
          <div className="ring-card-body">
            <ProgressRing
              value={totalContent}
              max={contentTarget}
              size={150}
              thickness={14}
              color="#10b981"
              label="Filled"
              sublabel={`${totalContent} / ${contentTarget}`}
            />
          </div>
        </div>

        <div className="chart-card stats-card">
          <div className="chart-card-head">
            <div>
              <h3 className="chart-card-title">Totals</h3>
              <span className="chart-card-sub">Live counters</span>
            </div>
          </div>
          <ul className="mini-stats">
            <li>
              <span className="mini-stats-label">Total Content</span>
              <span className="mini-stats-value">{totalContent}</span>
            </li>
            <li>
              <span className="mini-stats-label">Modules Active</span>
              <span className="mini-stats-value">
                {donutData.filter((d) => d.value > 0).length} / {donutData.length}
              </span>
            </li>
            <li>
              <span className="mini-stats-label">New Inquiries</span>
              <span className="mini-stats-value">{messagesCount}</span>
            </li>
          </ul>
        </div>
      </section>

      {/* ==================== SPLIT: ACTIVITY + QUICK ACTIONS ==================== */}
      <section className="dashboard-split">
        {/* Recent activity */}
        <div className="panel">
          <div className="panel-header">
            <div>
              <h3>Recent Contact Inquiries</h3>
              <span className="panel-sub">
                Latest messages received from the site
              </span>
            </div>
            <button className="ghost-btn" onClick={() => goTo('messages')}>
              View All →
            </button>
          </div>

          <div className="panel-body tight">
            {loading ? (
              <div className="skeleton-list">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div className="skeleton-row" key={i}>
                    <div className="sk-circle" />
                    <div>
                      <div className="sk-bar" />
                      <div className="sk-bar short" />
                    </div>
                    <div className="sk-bar pill" />
                  </div>
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-state">
                <span className="icon">📭</span>
                <span className="title">No inquiries yet</span>
                <span className="hint">
                  Messages submitted through the contact form will appear here.
                </span>
              </div>
            ) : (
              <div className="activity-list">
                {messages.map((item) => (
                  <div className="activity-row" key={item._id}>
                    <div className="activity-avatar">
                      {initialsOf(item.name || item.email)}
                    </div>
                    <div className="activity-main">
                      <p className="activity-name">
                        {item.name || 'Anonymous'}
                        {item.subject && (
                          <span className="activity-subject">{item.subject}</span>
                        )}
                      </p>
                      <p className="activity-sub">{item.email}</p>
                    </div>
                    <span className="activity-date">
                      {formatDate(item.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="panel">
          <div className="panel-header">
            <div>
              <h3>Quick Actions</h3>
              <span className="panel-sub">Jump straight into a module</span>
            </div>
          </div>
          <div className="panel-body">
            <div className="quick-actions">
              {QUICK_ACTIONS.map((q) => (
                <button
                  key={q.key}
                  className="quick-action"
                  onClick={() => goTo(q.key)}
                >
                  <span className="qa-icon">{q.icon}</span>
                  <span className="qa-text">
                    <span className="qa-title">{q.title}</span>
                    <span className="qa-sub">{q.sub}</span>
                  </span>
                  <span className="qa-chevron">→</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Metric Card with animated count-up + sparkline                              */
/* -------------------------------------------------------------------------- */

function MetricCard({ metric, value, trend, sparkSeed, delay, onClick }) {
  const { value: animated, ref } = useCountUp(value, 900);

  return (
    <button
      ref={ref}
      type="button"
      className="metric-card"
      onClick={onClick}
      aria-label={`Open ${metric.label}`}
      style={{
        animation: `metricCardIn 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms both`,
      }}
    >
      <div className="metric-top">
        <span className={`metric-icon tone-${metric.tone}`}>{metric.icon}</span>
        <span className={`metric-trend ${trend.type}`}>{trend.text}</span>
      </div>
      <p className="metric-label">{metric.label}</p>
      <div className="metric-value-row">
        <p className="metric-value">{animated}</p>
        <Sparkline
          values={sparkSeed}
          color={metric.color}
          width={90}
          height={30}
        />
      </div>
      <div className="metric-foot">
        <span>Tap to manage →</span>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function initialsOf(str = '') {
  return (
    str
      .split(/[\s@.]+/)
      .filter(Boolean)
      .map((s) => s[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}